import { AddIcon } from "@chakra-ui/icons";
import {
  Flex,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  TagLabel,
} from "@chakra-ui/react";
import { useField } from "formik";
import React, { useState } from "react";
import { SelectedItemTag } from "./SelectedItemTag";

type Props = {
  name: string;
  label?: React.ReactNode;
  labelProps?: FormLabelProps;
  placeholder?: string;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
  containerProps?: FlexProps;
  selectedItemsContainerProps?: FlexProps;
  isInvalidItem?: (value: string) => boolean;
  getInvalidItemMessage?: (value: string) => string | undefined;
  duplicateErrorMessage?: string;
};

export function TagTextInput({
  name,
  label,
  labelProps,
  placeholder = "Type and press Enter",
  isDisabled = false,
  size = "md",
  containerProps,
  selectedItemsContainerProps,
  isInvalidItem,
  getInvalidItemMessage,
  duplicateErrorMessage = "Duplicate values are not allowed",
}: Props) {
  const [{ value }, meta, helpers] = useField<string[]>(name);
  const [inputValue, setInputValue] = useState("");

  const values = value ?? [];
  const showError = Boolean(meta.touched && meta.error);

  const commitRawValues = async (rawValues: string[]): Promise<boolean> => {
    const nextValues = [...values];
    let changed = false;
    let hasDuplicate = false;

    for (const rawValue of rawValues) {
      const value = rawValue.trim();
      if (value === "") {
        continue;
      }
      if (nextValues.includes(value)) {
        hasDuplicate = true;
        continue;
      }
      nextValues.push(value);
      changed = true;
    }

    if (changed) {
      await helpers.setValue(nextValues);
    }

    if (changed || hasDuplicate) {
      await helpers.setTouched(true, false);
    }

    if (hasDuplicate) {
      await helpers.setError(duplicateErrorMessage);
      return true;
    }

    return false;
  };

  const commitPendingInput = async () => {
    const hasDuplicate = await commitRawValues([inputValue]);
    if (!hasDuplicate) {
      setInputValue("");
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (meta.error === duplicateErrorMessage) {
      void helpers.setError(undefined);
    }

    const nextValue = event.target.value;
    if (!nextValue.includes(",")) {
      setInputValue(nextValue);
      return;
    }

    const segments = nextValue.split(",");
    const remainder = segments.pop() ?? "";
    void commitRawValues(segments);
    setInputValue(remainder);
  };

  return (
    <FormControl
      as={Flex}
      flexDirection="column"
      gap="2"
      padding="1px"
      isInvalid={showError}
      {...containerProps}
    >
      {label !== undefined ? (
        <FormLabel margin="0" {...labelProps}>
          {label}
        </FormLabel>
      ) : null}
      {values.length > 0 ? (
        <Flex gap={1} flexWrap="wrap" {...selectedItemsContainerProps}>
          {values.map((value, index) => (
            <SelectedItemTag
              key={value}
              selectedItem={{ id: value, label: value }}
              index={index}
              getSelectedItemProps={() => ({})}
              isInvalid={isInvalidItem?.(value) ?? false}
              invalidItemMessage={getInvalidItemMessage?.(value)}
              size={size}
              isDisabled={isDisabled}
              onClose={(event) => {
                event.stopPropagation();
                void helpers.setValue(
                  values.filter((entry) => entry !== value)
                );
              }}
              renderTagContent={(item) => <TagLabel>{item.label}</TagLabel>}
            />
          ))}
        </Flex>
      ) : null}
      <InputGroup size={size}>
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              return;
            }
            // Enter in a text field inside Formik <Form> submits the parent form.
            event.preventDefault();
            void commitPendingInput();
          }}
          onBlur={() => {
            if (inputValue.trim() !== "") {
              void commitPendingInput();
            }
            void helpers.setTouched(true);
          }}
          placeholder={placeholder}
          size={size}
          isDisabled={isDisabled}
          pr="2.5rem"
          aria-label={
            typeof label === "string" ? label : "Enter multiple values"
          }
        />
        <InputRightElement width="2.5rem">
          <IconButton
            type="button"
            aria-label="Add value"
            icon={<AddIcon />}
            size="sm"
            variant="ghost"
            isDisabled={isDisabled || inputValue.trim() === ""}
            onClick={() => {
              void commitPendingInput();
            }}
          />
        </InputRightElement>
      </InputGroup>
      {showError ? (
        <FormErrorMessage margin="0">{meta.error}</FormErrorMessage>
      ) : null}
    </FormControl>
  );
}
